import { useAmplitudeExperiment } from '@/hooks/use-experiments';
import { initDatadogRum } from '@/libs/datadog/rum';
import { AmplitudeFeatureFlagNames } from '@/libs/feature-flags';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import useAuth from '@rhg/libs/auth0/use-auth';
import { Box } from '@rhg/libs/material';
import { ColorMode } from '@rhg/libs/material/theme';
import ThemeProvider from '@rhg/libs/material/theme-provider';
import { routePattern, routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { ReactNode, createContext } from 'react';

initDatadogRum();

type PageThemeEnforcement = Record<
    string,
    { unauthenticated?: ColorMode; authenticated?: ColorMode }
>;

const enforcedPageThemes: PageThemeEnforcement = {
    [routes.registration]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.reconsent]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.sso]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.ssoInfo]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.saml]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.partnerMemberActivation]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routePattern.partnerMemberActivationToken]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.dashboard]: { unauthenticated: 'light', authenticated: undefined },
    [routes.standaloneScreening]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.standaloneResults]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.nonUsResident]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.login]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.signup]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
};

function pageThemeMode(
    pathname: string,
    isAuthenticated: boolean,
    enforcedPageThemes: PageThemeEnforcement
) {
    const pageEnforcement = enforcedPageThemes[pathname];
    if (!pageEnforcement) {
        return undefined;
    }
    return isAuthenticated
        ? pageEnforcement.authenticated
        : pageEnforcement.unauthenticated;
}

export interface EnforcedThemeState {
    enforcedThemeMode?: ColorMode;
}
export const EnforcedThemeStateContext = createContext<EnforcedThemeState>(
    null!
);

export const EnforcedThemeProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const {
        isEnrolled: isThemeSelectorEnabled,
        isFetched: isThemeSelectorFetched,
    } = useAmplitudeExperiment({
        experimentName: AmplitudeFeatureFlagNames.THEME_SELECTOR_ENABLED,
    });
    const { isAuthenticated, isLoading } = useAuth();
    const { pathname, query } = useRouter();

    const enforcedThemeMode = pageThemeMode(
        pathname,
        isAuthenticated,
        enforcedPageThemes
    );

    // `isFetched` (unlike `isLoading`) never regresses once true: a
    // background user-property resync flips `isLoading` back to true but
    // leaves `isFetched`/`isEnrolled` untouched, so gating on it here keeps
    // the theme from falling back to the system default mid-session.
    const theme = useProvidedTheme(
        enforcedThemeMode,
        isThemeSelectorFetched && isThemeSelectorEnabled,
        query.theme as string | undefined
    );

    return (
        <EnforcedThemeStateContext.Provider value={{ enforcedThemeMode }}>
            <ThemeProvider theme={theme}>
                {isLoading ? (
                    <Box sx={{ position: 'fixed', left: 0, top: 0 }}>
                        <FullWidthLinearProgress />
                    </Box>
                ) : (
                    children
                )}
            </ThemeProvider>
        </EnforcedThemeStateContext.Provider>
    );
};
